import React from 'react'
import _ from 'lodash'

import {
    Alert,
    Button,
    Caption,
    Card,
    Colors,
    Divider,
    H3,
    H5,
    H6,
    Link,
    ListItem,
    P,
    Panel,
    Popup,
    S2,
    SortableList
} from '../../../../components/UI/index.js'
import {
    formSpacing,
    FormStack,
    Input,
    Toggle,
    UploadImage
} from '../../../../components/UI/forms/index.js'

import AddressModal from '../../../../components/addresses/AddressModal.js'
import LoginActions from '../../../../actions/LoginActions.js'
import {openMfaPopup} from '../../../../components/login/mfaPopup.js'
import showTwoFactorWarning from '../../../../components/login/twoFactorWarning.js'
import {Branch, Reseller} from '../../../../../server/functions/resellers/resellers.types.js'
import {User} from '../../../../../server/functions/users/users.types.js'
import {validateUrl} from '../../../../../utils/utils.js'
import {Address} from 'types/types.js'
import Languages from '@utils/Languages.js'
import t from 'src/helpers/Languages.js'
import EditBranchModal, {EditBranchModalRef} from '@components/settings/EditBranchModal.js'
import guid from '@utils/guid.js'
import {showBranchColumns} from 'src/utils/branches.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import type {PopupRef} from '../../../../components/UI/overlays/Popup.js'

interface CompanySettingsProps {
    reseller: Reseller
    onChange: (reseller: Reseller, path?: string) => void
    user: User & {index: number}

}

interface CompanySettingsState {
    addressModalIsSublocation: boolean
}

class AddressSettings extends React.Component<CompanySettingsProps, CompanySettingsState> {
    addressModal: AddressModal
    branchModal: EditBranchModalRef
    popup: PopupRef

    constructor(props) {
        super(props)

        this.state = {
            addressModalIsSublocation: false
        }
    }

    onChangeLogo(logo) {
        const {reseller, onChange} = this.props
        reseller.settings.logo = logo
        onChange(reseller, 'logo')
    }

    onChangeLogoBlack(logo) {
        const {reseller, onChange} = this.props
        reseller.settings.logoBlack = logo
        onChange(reseller, 'logoBlack')
    }

    onChangeMainLogo(logo) {
        const {reseller, onChange} = this.props
        reseller.settings.mainLogo = logo
        onChange(reseller, 'mainLogo')
    }

    onChangeSquareLogo(logo) {
        const {reseller, onChange} = this.props
        reseller.settings.squareLogo = logo
        onChange(reseller, 'squareLogo')
    }

    onChangeSizedLogo(path, logo) {
        const {reseller, onChange} = this.props
        _.set(reseller.settings, path, logo)
        onChange(reseller, path)
    }

    onClickEditCompanyAddress(address) {
        this.setState({addressModalIsSublocation: false}, () => {
            this.addressModal.open(address, (address) => {
                const {reseller, onChange} = this.props
                reseller.settings.companyAddress = address
                onChange(reseller, 'companyAddress')
                this.addressModal.close()
            })
        })
    }

    onClickNewSubLocation() {
        this.setState({addressModalIsSublocation: true})
        this.addressModal.open(undefined, this.onSaveSubLocation.bind(this))
    }

    onClickEditSubLocation(address: Address, index: number | undefined) {
        this.setState({addressModalIsSublocation: true})
        this.addressModal.open(address, (address: Address) => this.onSaveSubLocation(address, index), () => this.onRemoveSubLocation(index))
    }

    onRemoveSubLocation(index: number) {
        const {reseller, onChange} = this.props

        if (index !== undefined) {
            reseller.settings.sublocations.splice(index, 1)
            onChange(reseller, 'sublocations')
        }

        this.addressModal.close()
    }

    onSaveSubLocation(address: Address, index: number | undefined) {
        const {reseller, onChange} = this.props

        if (index !== undefined) {
            reseller.settings.sublocations[index] = address
        } else {
            reseller.settings.sublocations.push(address)
        }

        onChange(reseller, 'sublocations')
        this.addressModal.close()
    }

    onChangeChecked(path: string, event) {
        const reseller = {...this.props.reseller}
        const {user} = this.props

        if ((path === 'mfaWebEnabled' && !event.target.checked) || (path === 'mfaAppEnabled' && !event.target.checked && reseller.settings.mfaWebEnabled)) {
            if (user.mfaEnabled) {
                if (user.mfaMethod === 'email') {
                    LoginActions.mailMFACode(null, user.index, null, (err) => {
                        if (err) {
                            this.popup.setError(err)
                        }
                    })
                }

                openMfaPopup(this.popup, {
                    mfaMethod: user.mfaMethod,
                    confirmLabel: 'Bevestig',
                    onLogin: (code) => {
                        LoginActions.verify2FACode(code, (err) => {
                            if (err) {
                                this.popup.setError(err)
                            } else {
                                this.popup.close()

                                _.set(reseller.settings, path, event.target.checked)

                                this.props.onChange(reseller, path)
                            }
                        })
                    }
                })
            } else {
                showTwoFactorWarning(this.popup, () => LoginActions.logout())
            }
        } else if (path === 'brandingEnabled') {
            if (event.target.checked) {
                this.popup.open('Eigen branding activeren', 'Na activeren kunnen gebruikers en klanten alleen nog inloggen via de eigen URL.\n\nDe eigen branding module wordt geactiveerd voor gebruikers.', () => {
                    _.set(reseller.settings, path, event.target.checked)

                    this.props.onChange(reseller, path)
                    this.popup.close()
                })
            }
        } else {
            _.set(reseller.settings, path, event.target.checked)

            this.props.onChange(reseller, path)
        }
    }

    onChangeValue(path: string, event) {
        const reseller = {...this.props.reseller}

        _.set(reseller.settings, path, event.target.value)

        this.props.onChange(reseller)
    }

    onSaveValue<T, R>(path: string, event, parser?: (value: T) => R) {
        const reseller = {...this.props.reseller}

        if (event) {
            const value = typeof parser === 'function' ? parser(event.target.value as T) : event.target.value
            _.set(reseller.settings, path, value)
        } else if (typeof parser === 'function') {
            const currentValue = _.get(reseller.settings, path)
            if (currentValue) {
                _.set(reseller.settings, path, parser(currentValue as T) as R)
            }
        }

        this.props.onChange(reseller, path)
    }

    addBranch() {
        const reseller = {...this.props.reseller}

        const branch: Branch = {
            id: guid.generate(),
            name: '',
            address: {
                name: '',
                street: '',
                nr: '',
                addition: '',
                postalCode: '',
                city: '',
                country: reseller.settings.companyAddress?.country || 'NL'
            }
        }

        this.branchModal.open(branch)
    }

    render() {
        const {addressModalIsSublocation} = this.state
        const {reseller} = this.props

        return (
            <Card shadow style={{width: 600, padding: 24, height: 'fit-content'}}>
                <H3 style={{marginBottom: 36}}>Bedrijfsgegevens</H3>

                <Input
                    label='Accountnaam'
                    info=''
                    value={reseller.settings.accountName}
                    onChange={this.onChangeValue.bind(this, 'accountName')}
                    onBlur={this.onSaveValue.bind(this, 'accountName')}
                />

                <H5 style={{marginBottom: 24, marginTop: 36}}>Inlogbeveiliging</H5>

                <FormStack>
                    <Toggle
                        label='Tweefactorauthenticatie voor web omgeving'
                        checked={reseller.settings.mfaWebEnabled}
                        onChange={this.onChangeChecked.bind(this, 'mfaWebEnabled')}
                    />

                    <Toggle
                        label='Tweefactorauthenticatie voor koerier app'
                        checked={reseller.settings.mfaAppEnabled}
                        onChange={this.onChangeChecked.bind(this, 'mfaAppEnabled')}
                    />

                    {(reseller.settings.mfaWebEnabled || reseller.settings.mfaAppEnabled) &&
                        <Caption>Het eigenaar-account van {reseller.settings.accountName || reseller.name} kan per gebruiker de 2FA methode resetten.
                            {reseller.ownerEmail &&
                                <>Het eigenaar-account is: {reseller.ownerEmail}</>
                            }
                        </Caption>
                    }
                </FormStack>

                <H5 style={{marginBottom: 24, marginTop: 36}}>Logo en kleur</H5>

                <FormStack>
                    <UploadImage
                        label='Scherm logo'
                        subText={<S2>Bestandstype: png</S2>}
                        info='Dit logo komt op verschillende plekken terug. Bijvoorbeeld bij aangemaakte zendingen en op de Track & Trace pagina.'
                        image={reseller.settings.logo}
                        onChange={this.onChangeLogo.bind(this)}
                    />

                    <UploadImage
                        label='Print logo'
                        subText={<S2>Bestandstype: png</S2>}
                        info='Dit logo is optioneel en wordt gebruikt op labels in plaats van het standaard logo. Een zwart-wit logo met maximaal contrast heeft de voorkeur in verband met de eigenschappen van labelprinters. Om het logo zo groot mogelijk op het label te krijgen kan een beeldverhouding van 2:1 gebruikt worden.'
                        image={reseller.settings.logoBlack}
                        onChange={this.onChangeLogoBlack.bind(this)}
                    />

                    {!reseller.settings.logo &&
                        <Alert variant='info' style={{marginTop: -10, marginBottom: 18, maxWidth: 658}}>Je hebt nog geen scherm logo geupload, dit logo wordt op verschillende plekken gebruikt.</Alert>
                    }

                    {reseller.settings.logo && !reseller.settings.logoBlack &&
                        <Alert variant='info' style={{marginTop: -10, marginBottom: 18, maxWidth: 658}}>Je hebt nog geen print logo geupload, dit logo wordt gebruikt op de verzendlabels.</Alert>
                    }

                    <Input
                        type='color'
                        label='Bedrijfskleur'
                        info='Kies hier de kleur van jouw bedrijf, deze kleur wordt op verschillende plekken gebruikt. Bijv. op de Track & Trace pagina'
                        value={reseller.settings.color || Colors.brandColor}
                        onChange={this.onChangeValue.bind(this, 'color')}
                        onBlur={this.onSaveValue.bind(this, 'color')}
                    />
                </FormStack>

                {reseller.customBrandingEnabled ?
                    <div style={{position: 'relative'}}>
                        <H6 style={{marginBottom: 12, marginTop: 48}}>{t('customBranding')}</H6>
                        <Divider style={{marginBottom: 12}} />

                        <div style={{marginBottom: 24}}>
                            {!reseller.settings.brandingEnabled &&
                                <Button
                                    onClick={this.onChangeChecked.bind(this, 'brandingEnabled', {target: {checked: true}})}
                                >
                                    {t('activateCustomBrandingForUsers')}
                                </Button>
                            }
                        </div>

                        <Alert variant='info' style={{marginTop: -10, marginBottom: 30, maxWidth: 658}}>
                            {t('customBrandingLogosInfo')}
                        </Alert>

                        <FormStack style={{marginBottom: formSpacing.rowGap}}>
                            <UploadImage
                                label={
                                    <>
                                        {t('logoLoginPage')}
                                        <S2 style={{whiteSpace: 'initial'}}>{t('logoLoginPageDimensions')}
                                            <br />{t('fileTypePngSvg')}
                                        </S2>
                                    </>
                                }
                                image={reseller.settings.mainLogo}
                                onChange={this.onChangeMainLogo.bind(this)}
                                minHeight={128}
                                maxHeight={128}
                                minWidth={128}
                                maxWidth={256}
                                showDimensionsError
                                accept='image/png,image/svg+xml'
                            />

                            <UploadImage
                                label={
                                    <>
                                        {t('logoNavigationBar')}
                                        <S2 style={{whiteSpace: 'initial'}}>{t('logoNavigationBarDimensions')} <br />{t('fileTypePngSvg')}</S2>
                                    </>
                                }
                                image={reseller.settings.squareLogo}
                                onChange={this.onChangeSquareLogo.bind(this)}
                                minHeight={32}
                                maxHeight={32}
                                minWidth={32}
                                maxWidth={64}
                                showDimensionsError
                                accept='image/png,image/svg+xml'
                            />

                            <UploadImage
                                label={`${t('logo')} 32x32`}
                                subText={<S2>{t('fileTypePng')}</S2>}
                                image={reseller.settings.logos?.size_32}
                                onChange={this.onChangeSizedLogo.bind(this, 'logos.size_32')}
                                minHeight={32}
                                maxHeight={32}
                                minWidth={32}
                                maxWidth={32}
                                showDimensionsError
                                accept='image/png'
                            />

                            <UploadImage
                                label={`${t('logo')} 128x128`}
                                subText={<S2>{t('fileTypePng')}</S2>}
                                image={reseller.settings.logos?.size_128}
                                onChange={this.onChangeSizedLogo.bind(this, 'logos.size_128')}
                                minHeight={128}
                                maxHeight={128}
                                minWidth={128}
                                maxWidth={128}
                                showDimensionsError
                                accept='image/png'
                            />

                            <UploadImage
                                label={`${t('logo')} 180x180`}
                                subText={<S2>{t('fileTypePng')}</S2>}
                                image={reseller.settings.logos?.size_180}
                                onChange={this.onChangeSizedLogo.bind(this, 'logos.size_180')}
                                minHeight={180}
                                maxHeight={180}
                                minWidth={180}
                                maxWidth={180}
                                showDimensionsError
                                accept='image/png'
                            />

                            <UploadImage
                                label={`${t('logo')} 192x192`}
                                subText={<S2>{t('fileTypePng')}</S2>}
                                image={reseller.settings.logos?.size_192}
                                onChange={this.onChangeSizedLogo.bind(this, 'logos.size_192')}
                                minHeight={192}
                                maxHeight={192}
                                minWidth={192}
                                maxWidth={192}
                                showDimensionsError
                                accept='image/png'
                            />

                            <UploadImage
                                label={`${t('logo')} 512x512`}
                                subText={<S2>{t('fileTypePng')}</S2>}
                                image={reseller.settings.logos?.size_512}
                                onChange={this.onChangeSizedLogo.bind(this, 'logos.size_512')}
                                minHeight={512}
                                maxHeight={512}
                                minWidth={512}
                                maxWidth={512}
                                showDimensionsError
                                accept='image/png'
                            />
                        </FormStack>

                        <Alert style={{marginBottom: 6}} variant='info'>
                            {t('customManualsInfo')}
                        </Alert>

                        <FormStack>
                            <Input
                                label={t('manualUrlReseller')}
                                value={reseller.settings.manualURLReseller || ''}
                                onChange={this.onChangeValue.bind(this, 'manualURLReseller')}
                                onBlur={(event) => this.onSaveValue('manualURLReseller', event, validateUrl)}
                            />

                            <Input
                                label={t('manualUrlCustomer')}
                                value={reseller.settings.manualURLCustomer || ''}
                                onChange={this.onChangeValue.bind(this, 'manualURLCustomer')}
                                onBlur={(event) => this.onSaveValue('manualURLCustomer', event, validateUrl)}
                            />

                            <Input
                                label={t('manualUrlMessenger')}
                                value={reseller.settings.manualURLMessenger || ''}
                                onChange={this.onChangeValue.bind(this, 'manualURLMessenger')}
                                onBlur={(event) => this.onSaveValue('manualURLMessenger', event, validateUrl)}
                            />
                        </FormStack>

                    </div> :
                    !reseller.invoiceResellerId && reseller.accountType === 'carrierPlus' &&
                    <>
                        <H5 style={{marginBottom: 24, marginTop: 36}}>{Languages.customBranding['NL']}</H5>

                        <P style={{marginBottom: 24}}>
                            {Languages.brandingModuleExplanation['NL']} <Link href='https://handleiding.veloyd.nl/docs/eigen-branding/'>https://handleiding.veloyd.nl/docs/eigen-branding/</Link>
                            <br />
                            <br />
                            {Languages.brandingModuleActivation['NL']}
                        </P>

                        <Button
                            onClick={() => window.open(`mailto:info@veloyd.nl?subject=Aanvragen van Eigen branding voor ${reseller.settings.accountName || reseller.name}`)}
                            label='Branding module aanvragen'
                        />
                    </>
                }

                <H5 style={{marginBottom: 24, marginTop: 36}}>Adres</H5>

                <Panel>
                    <ListItem
                        style={{height: 'fit-content'}}
                        onClick={this.onClickEditCompanyAddress.bind(this, reseller.settings.companyAddress)}
                    >
                        {reseller.settings.companyAddress &&
                            (
                                <div>
                                    <p>
                                        {reseller.settings.companyAddress.name}
                                        <br />
                                        {`${reseller.settings.companyAddress.street} ${reseller.settings.companyAddress.nr} ${reseller.settings.companyAddress.addition}`}
                                        <br />
                                        {`${reseller.settings.companyAddress.postalCode} ${reseller.settings.companyAddress.city}`}
                                        <br />
                                        {`${reseller.settings.companyAddress.email || ''} ${reseller.settings.companyAddress.phone ? ` | ${reseller.settings.companyAddress.phone}` : ''}`}
                                    </p>
                                </div>
                            )
                        }
                    </ListItem>
                </Panel>

                {branchesAvailable(reseller) ?
                    <>
                        <H5 style={{marginBottom: 24, marginTop: 24}}>{Languages.branches['NL']}</H5>

                        {!!reseller.settings.branches?.length &&
                            <Panel style={{maxHeight: 500, marginTop: 36, flex: 1, overflowY: 'auto', marginBottom: 24}}>
                                <SortableList
                                    items={reseller.settings.branches || []}
                                    onChange={(branches) => {
                                        reseller.settings.branches = branches
                                        this.props.onChange(reseller, 'branches')
                                    }}
                                    renderItem={(branch) => {
                                        return (
                                            <ListItem
                                                key={`branch${branch.id}`}
                                                onClick={() => this.branchModal.open(branch)}
                                                icon='mdi mdi-drag-horizontal'
                                            >
                                                <P ellipsis>
                                                    {branch.name}
                                                </P>
                                            </ListItem>
                                        )
                                    }}
                                />
                            </Panel>
                        }

                        <Button
                            variant='outline'
                            icon='mdi mdi-plus'
                            label={Languages.addBranch['NL']}
                            onClick={() => this.addBranch()}
                        />
                    </> :
                    <>

                        <H5 style={{marginBottom: 24, marginTop: 36}}>Andere locaties</H5>

                        {reseller.settings.sublocations && reseller.settings.sublocations.length > 0 &&
                            (
                                <Panel style={{marginBottom: 24, maxHeight: 500, overflowY: 'auto'}}>
                                    {reseller.settings.sublocations.map((address, index) => (
                                        <ListItem onClick={this.onClickEditSubLocation.bind(this, address, index)} style={{height: 'fit-content'}} key={index}>
                                            <div>
                                                <p>
                                                    {address.name}
                                                    <br />
                                                    {`${address.street} ${address.nr} ${address.addition}`}
                                                    <br />
                                                    {`${address.postalCode} ${address.city}`}
                                                </p>
                                            </div>
                                        </ListItem>
                                    ))}
                                </Panel>
                            )
                        }

                        <Button
                            variant='outline'
                            icon='mdi mdi-plus'
                            label='Nieuwe locatie'
                            onClick={this.onClickNewSubLocation.bind(this)}
                        />
                    </>
                }

                <AddressModal
                    showEmail={!addressModalIsSublocation}
                    showPhone={!addressModalIsSublocation}
                    showBusinessCheckbox={false}
                    noTAV
                    ref={(ref) => this.addressModal = ref}
                />

                <EditBranchModal
                    reseller={reseller}
                    onChange={(branch) => {
                        const reseller = {...this.props.reseller}

                        branch.name = branch.address.name

                        reseller.settings.branches = reseller.settings.branches || []

                        const index = reseller.settings.branches?.findIndex((p) => p.id === branch.id)
                        const isNewBranch = typeof index !== 'number' || index === -1
                        const isFirstBranch = isNewBranch && reseller.settings.branches.length === 0

                        if (!isNewBranch) {
                            reseller.settings.branches[index] = branch
                        } else {
                            reseller.settings.branches?.push(branch)
                        }

                        // Without this the branch column stays hidden everywhere and gets overlooked
                        if (isFirstBranch) {
                            showBranchColumns()
                        }

                        this.props.onChange(reseller, 'branches')
                    }}
                    ref={(ref) => this.branchModal = ref}
                />

                <Popup ref={(ref) => this.popup = ref} />
            </Card>
        )
    }
}

export default AddressSettings
